import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { environment } from 'environments/environment';

declare global {
    interface Window {
        fbq: (...args: unknown[]) => void;
    }
}

export const META_PIXEL_ID = '187886681575415';

/**
 * Service for Meta (Facebook) Pixel event tracking.
 *
 * The pixel itself is installed with the official snippet in index.html, for the
 * same reason as the Google tag: tracked events fire on clicks that navigate away
 * (tel:, mailto:, route changes), and a pixel injected at click time has not
 * finished downloading in time to send them. This service only rides on top of it.
 *
 * No-ops on server for SSR compatibility.
 * Respects environment.analytics.enabled and environment.analytics.metaPixel.enabled flags.
 */
@Injectable({
    providedIn: 'root'
})
export class MetaPixelService {
    private _platformId = inject(PLATFORM_ID);
    private _document = inject(DOCUMENT);

    private _initialised = false;
    private _initialPageViewHandled = false;

    init(): void {
        this.ensurePixel();
    }

    /**
     * Make sure fbq() is available before we push anything at it.
     *
     * Normally the snippet in index.html has already defined it. If something
     * stripped the snippet we fall back to loading fbevents.js ourselves so
     * tracking degrades instead of disappearing.
     *
     * @returns true when it is safe to call window.fbq()
     */
    private ensurePixel(): boolean {
        if (!environment.analytics?.enabled || !environment.analytics?.metaPixel?.enabled) {
            return false;
        }

        if (!isPlatformBrowser(this._platformId)) {
            return false;
        }

        // See GtagService.ensureTag — index.html is the authority on which hosts
        // report, so UAT and localhost never reach the client's pixel.
        if (window.__spfAnalyticsEnabled === false) {
            return false;
        }

        if (this._initialised) {
            return typeof window.fbq === 'function';
        }

        this._initialised = true;

        if (typeof window.fbq === 'function') {
            return true;
        }

        const w = window as any;
        const fbqFunc: any = function (...args: any[]) {
            fbqFunc.callMethod ? fbqFunc.callMethod.apply(fbqFunc, args) : fbqFunc.queue.push(args);
        };
        fbqFunc.push = fbqFunc;
        fbqFunc.loaded = true;
        fbqFunc.version = '2.0';
        fbqFunc.queue = [];

        w.fbq = fbqFunc;
        w._fbq = fbqFunc;

        const script = this._document.createElement('script');
        script.async = true;
        script.src = 'https://connect.facebook.net/en_US/fbevents.js';
        this._document.head.appendChild(script);

        w.fbq('init', META_PIXEL_ID);
        w.fbq('track', 'PageView');

        return true;
    }

    /**
     * Fire a standard Meta Pixel event.
     * Safe to call on server (no-op).
     * Only sends if analytics is enabled in environment configuration.
     */
    track(eventName: string, params?: Record<string, unknown>): void {
        if (!this.ensurePixel()) {
            return;
        }

        // The snippet in index.html already fired PageView for the document the
        // browser was served, so the first router notification would duplicate it.
        if (eventName === 'PageView' && !this._initialPageViewHandled) {
            this._initialPageViewHandled = true;
            return;
        }

        if (params && Object.keys(params).length > 0) {
            window.fbq('track', eventName, params);
        } else {
            window.fbq('track', eventName);
        }
    }

    /**
     * Fire a custom Meta Pixel event (trackCustom).
     * Safe to call on server (no-op).
     * Only sends if analytics is enabled in environment configuration.
     */
    trackCustom(eventName: string, params?: Record<string, unknown>): void {
        if (!this.ensurePixel()) {
            return;
        }

        window.fbq('trackCustom', eventName, params ?? {});
    }
}
